// Timeout policy for field app network requests (ADR-0110, Card 2/4:
// "Timeout policy module"). Every AbortSignal.timeout(...) value in the field
// app used to be a hardcoded literal at its call site, tuned once (often on
// its own card, in isolation) and never revisited. timeoutFor(kind, level)
// centralises those numbers so a future card (Card 3, degraded-mode UX) can
// shorten calls when the connectivity level drops, without hunting through a
// dozen files.
//
// CARD 2 SCOPE: at level 2 (Online) every kind below returns EXACTLY the
// value that was hardcoded at its call site(s) before this card -- this is a
// pure extraction, not a behavioural change. Where two call sites that look
// alike at a glance in fact carried different pre-existing values, they were
// split into distinct kinds rather than merged onto one number, specifically
// to preserve that guarantee (see the per-kind comments below for exactly
// which call site(s) each row's Online value was measured from).
//
// Degraded-level (1) values exist now for Card 3 to consume -- nothing in the
// app branches on them yet. They are this card's own judgement call (fail
// fast on secondary/background traffic, stay generous on the primary
// response-data write and on image uploads, since aborting either loses real
// field work to the offline queue rather than just delaying it).
//
// Offline (0) is not distinguished from Degraded below: no call site's
// Online-level behaviour needs preserving at Offline (most callers already
// short-circuit to the local queue before ever reaching fetch when confirmed
// offline -- see field_image_queue.js, result_editor_controller.js), and the
// handful of "network-first" callers that still attempt a fetch while
// offline (e.g. _submitWithOfflineFallback) gain nothing from waiting the
// full Online timeout on a request very likely to fail outright.
//
// New leaf module, alongside field_network_health.js -- no imports from
// queue/sync modules, and (deliberately) no import of field_network_health.js
// either: kind and level are passed in by the caller, so this module stays a
// pure function with zero side-effecting imports of its own. Callers get the
// level via field_network_health.js#currentLevel() themselves.

// Mirrors field_network_health.js's LEVEL_ONLINE numbering (0 Offline / 1
// Degraded / 2 Online) by value, not by import -- see the leaf-module note
// above for why this file never imports that module.
const LEVEL_ONLINE = 2

// { [kind]: { online, degraded } } -- ms.
const POLICY = {
  // Background-sync replay of the primary survey/response data PUT
  // (field_upload_sync.js#replayResponse). The single most important write
  // in the app -- kept generous online; degraded is still long enough to let
  // a slow-but-alive connection land the record rather than abort into a
  // retry loop.
  json_write: { online: 15000, degraded: 8000 },

  // Lighter write calls that don't carry the full survey payload: the
  // response-completion POST and the image-URL patch PUT
  // (field_upload_sync.js), plus the foreground survey editor's own
  // PUT/POST equivalents (controllers/surveys/result_editor_controller.js).
  // All four were already independently tuned to the same 10000ms online.
  json_write_light: { online: 10000, degraded: 4000 },

  // The bad-connectivity toggle (controllers/field_bad_connectivity_controller.js)
  // -- a snappy, interactive PATCH the Tasker is actively waiting on. Kept
  // short online so a bad flag flip doesn't hang the UI; degraded is the
  // same short value since waiting longer wouldn't help an interactive toggle.
  toggle_write: { online: 8000, degraded: 4000 },

  // The Tasker pin correction (controllers/field_pin_correction_controller.js)
  // is one small interactive PATCH the Tasker is waiting on, with a Save button
  // they can press again. A little longer than toggle_write online because the
  // server also writes a PaperTrail version; short degraded so a weak signal
  // gets a plain message rather than a long spinner.
  pin_write: { online: 12000, degraded: 8000 },

  // Background image-URL patch GET (field_upload_sync.js#patchResultUrls).
  json_read: { online: 10000, degraded: 5000 },

  // My Photos gallery server-image load (controllers/field_photos_controller.js)
  // -- heavier payload (an image metadata list) than the plain json_read
  // above, hence the longer pre-existing online value.
  json_read_gallery: { online: 15000, degraded: 5000 },

  // Image upload/backup POSTs (field_image_queue.js#backupOrphan and
  // #_uploadImage, and the foreground uploadSingleFile in
  // result_editor_controller.js). Large multipart bodies -- generous online,
  // and still meaningfully long in Degraded since an aborted upload loses
  // the whole capture to the offline queue rather than merely delaying it.
  //
  // Online raised 30000 -> 60000 in the 2026-09-22 photo pipeline review: a
  // median post-#3518 photo at a throttled 50KB/s took ~30s, brushing the
  // old value, and every byte proxies through Rails to Linode (no direct-to-
  // storage PUT). rt-client's equivalent direct-to-S3 upload uses 60s.
  // Degraded is unchanged -- that path queues before ever reaching fetch.
  image_upload: { online: 60000, degraded: 15000 },

  // The first upload attempt at capture, sent from the bytes still in memory
  // (field_image_queue.js#uploadPhotoFromMemory, card #3994). Unlike
  // image_upload above, Degraded is LONGER than Online: this attempt is made
  // at exactly the places and moments the app used to skip the network for
  // photos (Poor Signal places, a degraded connection). Giving up early there
  // would push the photo into the on-device queue, which iPhones cannot
  // reliably read back, so a slow connection gets 45s to land the photo from
  // memory instead. Online stays short (20s): a healthy connection that has not
  // answered by then is better served by the queue's own 60s retry.
  image_upload_memory: { online: 20000, degraded: 45000 },

  // The same attempt when the Tasker no longer waits for it (card #4097,
  // field:background_photo_upload, decision D3). Nobody sits behind a popup,
  // so it gets the queue's 60s, and 90s at a Poor Signal place or on a
  // degraded connection, instead of 20s / 45s.
  image_upload_background: { online: 60000, degraded: 90000 },

  // Full prefetch download (controllers/field_prefetch_controller.js,
  // controllers/field_auto_sync_controller.js,
  // controllers/field_sync_page_controller.js). No Degraded value -- Card 3
  // pauses prefetch entirely in Degraded rather than running it with a
  // shorter timeout, so nothing should ever ask for this at level 1.
  prefetch: { online: 30000, degraded: null },

  // Background app-log batch sync (field_log_sync.js) -- low priority,
  // failures are silently retried next cycle (see that file's header
  // comment), so Degraded fails fast rather than competing with
  // higher-value traffic for a poor connection.
  log_sync: { online: 15000, degraded: 5000 },

  // Per-URL page/asset warming fetch inside the service worker's WARM_CACHE
  // message handler (field_service_worker.js) -- pre-caches each response,
  // mission, and place page (plus a handful of cross-origin CDN assets)
  // after a sync completes, ahead of the Tasker ever navigating there
  // offline. Previously had NO timeout at all -- a hang on a genuinely bad
  // connection (the exact scenario a bad_connectivity place exists to
  // simulate) left that page's offline shell uncached with zero trace
  // (Card 2899). Every URL is fetched in parallel via Promise.all inside a
  // single WARM_CACHE pass, so this needs to resolve well inside the
  // caller's own 15s overall safety timeout (field_sync_page_controller.js
  // #_warmPhase) -- matches json_read's values since both are light GETs
  // that aren't worth holding up the boat for.
  page_warm: { online: 10000, degraded: 5000 }
}

export function timeoutFor(kind, level) {
  const policy = POLICY[kind]
  if (!policy) {
    throw new Error(`timeoutFor: unknown kind "${kind}"`)
  }

  if (level === LEVEL_ONLINE) return policy.online
  // Degraded and Offline currently share a value -- see the Offline note
  // above. Falls back to the Online value for any kind (like prefetch) that
  // has no Degraded number of its own, rather than returning null/undefined
  // into AbortSignal.timeout().
  return policy.degraded ?? policy.online
}
